import type { TeamRoleName, WorkshopUserRole } from "../api/workshop";

export const Roles = Object.freeze({
  STUDENT: "student",
  TEACHER: "teacher",
  ADMIN: "admin",
}) satisfies Record<string, WorkshopUserRole>;

/** A user's roles as returned by the API — absent while unknown. */
type UserRoles = readonly WorkshopUserRole[] | null | undefined;

/** Every role, least to most privileged. `highestRole` picks the last one present. */
const ROLES_BY_PRECEDENCE: readonly WorkshopUserRole[] = [
  Roles.STUDENT,
  Roles.TEACHER,
  Roles.ADMIN,
];

/** The roles a person can hold in a team — `admin` is granted globally instead. */
const TEAM_ROLES: readonly TeamRoleName[] = [Roles.STUDENT, Roles.TEACHER];

/** A role name arriving from outside: a URL parameter, an API payload. */
type MaybeRoleName = string | null | undefined;

export function hasRole(roles: UserRoles, role: WorkshopUserRole): boolean {
  return Boolean(roles?.includes(role));
}

/**
 * The most privileged role the user holds, falling back to student — a user
 * with no roles sees the same thing a student does.
 */
export function highestRole(roles: UserRoles): WorkshopUserRole {
  return (
    ROLES_BY_PRECEDENCE.findLast((role) => hasRole(roles, role)) ??
    Roles.STUDENT
  );
}

export function isStudent(roles: UserRoles): boolean {
  return hasRole(roles, Roles.STUDENT);
}

/**
 * Whether the user may run the workshop: invite and organise participants,
 * change its settings, and use the classroom management tools.
 */
export function canManageWorkshop(roles: UserRoles): boolean {
  return hasRole(roles, Roles.TEACHER) || hasRole(roles, Roles.ADMIN);
}

/**
 * Whether an untrusted value names a role someone can register as. `admin` is
 * granted globally, never chosen, so it is deliberately rejected here.
 */
export function isTeamRoleName(value: MaybeRoleName): value is TeamRoleName {
  return TEAM_ROLES.some((role) => role === value);
}
